import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { BaseContextProps } from "./base-context";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation } from "react-query";
import { getTaskAssignment } from "../apis/taskApi";
import taskService from "../services/task.service";
import { TaskDetailProps, TypeProps } from "../types/task.type";

interface TaskDetailContextProps extends BaseContextProps {
  form: {
    isValidated: boolean
    validatedAt: number
    activeIndex: number | null
    setIsValidated: (value: boolean) => void
    setValidatedAt: (value: number) => void
    setActiveIndex: (value: number | null) => void
  }
  data: {
    task: TaskDetailProps | undefined
    taskType: TypeProps | undefined
  }
  refetch: {
    task: () => Promise<void>,
    taskType: () => Promise<void>
  }
}

interface TaskDetailContextProviderProps {
  children: ReactNode
}

export interface LocalActionTemplateProps {
  id: number
  value: string | File | undefined
  real_value?: string | File | undefined
  mode: 'REQUIRED' | 'OPTIONAL'
  is_conditional: boolean
  conditional_id: number | null
  conditional_operator: '=' | '!=' | '>' | '<' | '>=' | '<=' | null
  conditional_value: string | number | null
  section_id: number
  form_type: string
}

export interface TaskResultValueProps {
  action_id: number
  section_id: number
  action_template: LocalActionTemplateProps[]
}

const TaskDetailContext = createContext<TaskDetailContextProps>({
  isLoading: true,
  isError: false,
  errorMessage: '',
  form: {
    isValidated: false,
    validatedAt: 0,
    activeIndex: null,
    setIsValidated: () => {},
    setValidatedAt: () => {},
    setActiveIndex: () => {},
  },
  data: {
    task: undefined,
    taskType: undefined,
  },
  refetch: {
    task: async () => {}, 
    taskType: async () => {}, 
  }
})
export const useTaskDetailContext = () => useContext(TaskDetailContext)

const TaskDetailContextProvider: React.FC<TaskDetailContextProviderProps> = (props: TaskDetailContextProviderProps) => {
  const { children } = props

  const [searchParams] = useSearchParams()
  const { id } = useParams()

  const taskId = searchParams?.get('id') ?? ''

  const [isValidated, setIsValidated] = useState<boolean>(false)
  const [validatedAt, setValidatedAt] = useState<number>(0)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const taskTypeMutation = useMutation({
    mutationFn: taskService.type,
  })
  const taskMutation = useMutation({
    mutationFn: getTaskAssignment,
    onSuccess: (resp) => {
      console.log({resp})
      taskTypeMutation.mutate({ task_type_id: resp?.task?.task_type?.id })
    }
  })

  useEffect(() => {
    taskMutation.mutate({ task_assignment_id: Number(id) })
  }, [])

  return (
    <TaskDetailContext.Provider 
      value={{
        isLoading: 
          taskMutation?.isIdle ||
          taskMutation?.isLoading ||
          taskTypeMutation?.isIdle ||
          taskTypeMutation?.isLoading,
        errorMessage: 'Task Detail Context Error',
        isError: taskMutation?.isError || taskTypeMutation?.isError,
        form: {
          validatedAt: validatedAt,
          isValidated: isValidated,
          activeIndex: activeIndex,
          setIsValidated: (value: boolean) => setIsValidated(value),
          setValidatedAt: (value: number) => setValidatedAt(value),
          setActiveIndex: (value: number | null) => setActiveIndex(value)
        },
        data: {
          task: taskMutation?.data,
          taskType: taskTypeMutation?.data?.data
        },
        refetch: {
          task: async () => taskMutation.mutate({ task_assignment_id: Number(taskId) }),
          taskType: async () => taskTypeMutation.mutate({ task_type_id: taskMutation?.data?.data?.task?.task_type?.id??0 }),
        }
      }}
    >
      {children}
    </TaskDetailContext.Provider>
  )
}

export default React.memo(TaskDetailContextProvider)